/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * `NoteFile` type.
 */

type NoteFile = {
  isLeaf: boolean;
  key: string;
  title: string;
  children: NoteFile[] | undefined;
  content: string | undefined;
};

/**
 * Get content.
 */

function getContent(client: AxiosInstance, id: React.Key): Promise<NoteFile> {
  return client.get(resolveAppUrl('appsNotesById', { interpolations: { id } })).then(({ data }) => data);
}

/**
 * Export `useGetContentInvalidator`.
 */

export function useGetContentInvalidator() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries(['getNotesFileSystem']);
  }, [queryClient]);
}

/**
 * Export `useGetNotesContent`.
 */

export function useGetNotesContent(key: React.Key | null) {
  const client = useRequestProvider();

  return useQuery(['getNotesFileSystem', key], {
    queryFn: async () => {
      if (!key) {
        return null;
      }

      return await getContent(client, key);
    }
  });
}
